/**
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {MouseEvent} from 'react';
import {
  makeStyles,
  MenuItem
} from '@material-ui/core';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {StarsFillIcon} from '@ion/dls/src/icons/StarsFillIcon';
import {StarsIcon} from '@ion/dls/src/icons/StarsIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {IconButton} from '../../components/buttons/IconButton';
import {DLSTheme, IconSize} from '../../types/types';
import {SavedColumnItem} from './types';

interface SavedColumnsType {
  savedColumnViews: SavedColumnItem[];
  onItemSelect?: (view: SavedColumnItem) => void;
  onDelete?: (e: MouseEvent<HTMLButtonElement>, view: SavedColumnItem) => void;
  selectedViewName?: string;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    label : {
      whiteSpace   : 'nowrap',
      overflow     : 'hidden',
      textOverflow : 'ellipsis',
      fontSize     : 12,
      width        : 165
    },
    columnListItem : {
      gap                : 4,
      padding            : '3px 15px !important',
      borderBottom       : `1px solid ${dlsColor('grey5')}`,
      width              : 'auto',
      height             : 48,
      display            : 'flex',
      justifyContent     : 'space-between',
      '&:hover, &:focus' : {
        background : theme.dls.colors.grey5
      }
    },
    selectedListItem : {
      gap             : 4,
      padding         : '3px 15px !important',
      borderBottom    : `1px solid ${dlsColor('grey5')}`,
      backgroundColor : `${dlsColor('lightBlue')} !important`,
      width           : 'auto',
      height          : 48,
      display         : 'flex',
      justifyContent  : 'space-between'
    },
    labelWrap : {
      display    : 'flex',
      alignItems : 'center'
    }
  };
});

/**
 * @SavedColumns
 *
 * The SavedColumns provides a UI to render all saved column name in a list.
 *
 * ```typescript
 *
 *    <SavedColumns savedColumnViews={savedColumnViews}
 *      onItemSelect={onItemSelect}
 *      onDelete={onDelete}
 *      selectedViewName={selectedViewName} />
 * ```
 *
 * @see SavedColumnList
 */
export const SavedColumns = ({
  savedColumnViews,
  onItemSelect,
  onDelete,
  selectedViewName
}: SavedColumnsType) => {
  const classes = useStyles();
  return (
    <>
      {savedColumnViews?.map((view) => {
        const {viewName} = view;
        if (viewName) {
          const isSelected = viewName === selectedViewName;
          return (
            <MenuItem key={`saved-column-${viewName}`}
              classes={{root: isSelected ? classes.selectedListItem : classes.columnListItem}}
              onClick={function() {
                onItemSelect(view);
              }}>
              <div className={classes.labelWrap}>
                {isSelected
                  ? <StarsFillIcon size={IconSize.small} color={dlsColor('grey3')} />
                  : <StarsIcon size={IconSize.small}/>
                }
                <LabelTypography className={classes.label}>
                  <Tooltip arrow title={viewName?.length > 20 ? viewName : ''}>
                    <span>{viewName}</span>
                  </Tooltip>
                </LabelTypography>
              </div>
              <div>
                <IconButton icon={<MaterialExitCloseIcon size={IconSize.medium} />}
                  onClick={function(e) {
                    onDelete(e, view);
                  }}
                  color={dlsColor('grey3')} />
              </div>
            </MenuItem>
          );
        };
      })}
    </>
  );
};

export default SavedColumns;
